import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
// Utilities
import AttributionUtil from 'Utils/content/attribution';
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import DateUtil from 'Utils/date-util';
import { SPACING } from 'Style/spacing';
import { TEXT_COLORS, SURFACE_COLORS } from 'Style/colors';

// Components
import Title from '../../components/base/title';
import Image, { THEMES as IMAGE_THEMES } from '../../components/base/image';
import ItemOverlay from '../../components/item/card/item-overlay';
import ImageUnavailable from '../../components/image-unavailable';
import TwitterLogo from 'ComponentLibrary/logos/twitter';
import TwitterText from '../../components/twitter-text';
import Media from '../../components/base/media';
import Video from '../../components/base/video';
import Audio from '../../components/base/audio';
import ItemLink from '../../components/item/item-link';

import connector from 'Utils/connector';
import connectLanguage from 'Webapp/shared/app/connectors/connectLanguage';
import { UI_TEXT_TYPES } from 'Style/typography';

const { sizes } = StoryboardRenderingUtil;

const StyledStoryboardMediaWrapper = styled.div({
  position: 'relative',
  '.post__title': {
    marginBottom: '0',
  },
  '.media-container': {
    height: '100%',
  },
  '.responsive-iframe': {
    position: 'unset',
  },
});

const StyledStoryboardPostMetadata = styled.div({
  ...UI_TEXT_TYPES.SUPPORTING,
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: SPACING.LARGE,
});

const StyledStoryboardPostTwitterContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexGrow: '1',
  overflow: 'hidden',
  color: TEXT_COLORS.primary,
});

const StyledStoryBoardPostPreview = styled.div({
  '.image-frame, .storyboard-post__media-wrapper': {
    backgroundColor: SURFACE_COLORS.secondary,
  },
});

function renderTwitterTextElement(item, section, lang) {
  const metadataElement = (
    <StyledStoryboardPostMetadata>
      <time className="storyboard-post__timestamp">
        {DateUtil.completeFormat(item.dateCreated, lang)}
      </time>
      <TwitterLogo color="#FFFFFF" width={16} height={13} />
    </StyledStoryboardPostMetadata>
  );

  return (
    <StyledStoryboardPostTwitterContent>
      <div className="twitter-text post-excerpt--text">
        <TwitterText text={item.text} />
      </div>
      <ItemLink item={item} section={section} shownInList>
        {metadataElement}
      </ItemLink>
    </StyledStoryboardPostTwitterContent>
  );
}

function renderStatusPreview(item, section, group, index, lang) {
  const textElement = item.isTwitter ? (
    renderTwitterTextElement(item, section, lang)
  ) : (
    <Title
      primary={false}
      className="post__title post-excerpt--text"
      item={item}
    />
  );

  return (
    <StyledStoryboardMediaWrapper className="storyboard-post__media-wrapper">
      <div className="storyboard-post__title-wrapper">
        {StoryboardRenderingUtil.isNumberedGroup(group) && (
          <span className="storyboard-post__prefix article-text--title--small">
            {index + 1}.
          </span>
        )}
        {textElement}
      </div>
    </StyledStoryboardMediaWrapper>
  );
}

function ampHeight(group) {
  const size = StoryboardRenderingUtil.groupSize(group);

  if (size === sizes.SMALL) {
    return 80;
  }
  if (size === sizes.MEDIUM) {
    return 232;
  }
  if (size === sizes.LARGE) {
    return 282;
  }

  return 80;
}

function StoryboardPostPreview({
  item,
  section,
  group,
  index,
  fallbackElementSize,
  lang,
}) {
  const previewSize = StoryboardRenderingUtil.groupSize(group);
  const height = ampHeight(group);
  const { image } = item;

  // ImageUnavailable element for fallback image rendering, requires the author object
  const author = AttributionUtil.getAuthor(item);
  const fallbackElement = (
    <ImageUnavailable
      author={author}
      fallbackElementSize={fallbackElementSize}
    />
  );

  // Status item
  if (item.isStatus) {
    return (
      <StyledStoryBoardPostPreview className="storyboard-post__preview">
        {renderStatusPreview(item, section, group, index, lang)}
      </StyledStoryBoardPostPreview>
    );
  }

  if (item.isFirstPartyVideo) {
    return (
      <StyledStoryBoardPostPreview className="storyboard-post__preview">
        <StyledStoryboardMediaWrapper className="storyboard-post__media-wrapper">
          <Media
            item={item}
            section={section}
            shownInList
            fallbackElementSize={fallbackElementSize}
            previewSize={previewSize}
          />
        </StyledStoryboardMediaWrapper>
      </StyledStoryBoardPostPreview>
    );
  }

  // Video
  if (item.isVideo) {
    return (
      <StyledStoryBoardPostPreview className="storyboard-post__preview">
        <StyledStoryboardMediaWrapper className="storyboard-post__media-wrapper">
          {previewSize === sizes.SMALL ? (
            <ItemLink item={item} section={section} shownInList>
              <Image
                image={image}
                ampLayout="fill"
                ampHeight={height}
                alt={item.title}
                fallbackElement={fallbackElement}
                variant={IMAGE_THEMES.medium}
                crop
              />
              <ItemOverlay size={32} border item={item} showDuration={false} />
            </ItemLink>
          ) : (
            <Video
              item={item}
              ampLayout="fill"
              ampHeight={height}
              alt={item.title}
              fallbackElement={fallbackElement}
              shownInList
              previewSize={previewSize}
            />
          )}
        </StyledStoryboardMediaWrapper>
      </StyledStoryBoardPostPreview>
    );
  }

  // Audio
  if (item.isAudio) {
    return (
      <StyledStoryBoardPostPreview className="storyboard-post__preview">
        <StyledStoryboardMediaWrapper className="storyboard-post__media-wrapper">
          {StoryboardRenderingUtil.groupSize(group) === sizes.SMALL ? (
            <ItemLink item={item} section={section} shownInList>
              <Image
                image={image}
                ampLayout="fill"
                ampHeight={height}
                alt={item.title}
                fallbackElement={fallbackElement}
                variant={IMAGE_THEMES.medium}
                crop
              />
            </ItemLink>
          ) : (
            <Audio
              item={item}
              section={section}
              ampHeight={height}
              fallbackElement={fallbackElement}
              shownInList
            />
          )}
        </StyledStoryboardMediaWrapper>
      </StyledStoryBoardPostPreview>
    );
  }

  // Standard post
  return (
    <StyledStoryBoardPostPreview className="storyboard-post__preview">
      <div className="image-frame">
        <ItemLink item={item} section={section} shownInList>
          <Image
            image={image}
            ampLayout="fill"
            ampHeight={height}
            alt={item.title}
            fallbackElement={fallbackElement}
            variant={IMAGE_THEMES.large}
            crop
          />
        </ItemLink>
      </div>
    </StyledStoryBoardPostPreview>
  );
}

StoryboardPostPreview.propTypes = {
  item: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  group: PropTypes.object,
  index: PropTypes.number,
  fallbackElementSize: PropTypes.oneOf(
    Object.values(ImageUnavailable.fallbackElementSizes),
  ),
};

StoryboardPostPreview.defaultProps = {
  index: null,
  group: null,
  fallbackElementSize: ImageUnavailable.fallbackElementSizes.CONDENSED,
};

export default connector(connectLanguage)(StoryboardPostPreview);
